import Container from "Components/Container/Container";
import { useState } from "react";
import Input from "form/Inputs/Input";
import useInput from "form/Hooks/user-input";
import Textarea from "form/Inputs/Textarea";
import SelectInput from "form/Inputs/SelectInput";
import Spinner from "Components/RequestHandler/Spinner";
import BASE_URL from "Hooks/BASE_URL";

const Form = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [formWarning, setFormWarning] = useState("");

  const {
    value: fullNameInput,
    isValid: fullNameIsValid,
    HasError: fullNameHasError,
    inputChangeHandler: fullNameChangeHandler,
    inputBlurHandler: fullNameBlurHandler,
    inputFocusHandler: fullNameFocusHandler,
    reset: fullNameRest,
    isFocus: fullNameIsFocus,
  } = useInput((value) => value.trim() !== "");

  const {
    value: emailInput,
    isValid: emailIsValid,
    HasError: emailHasError,
    inputChangeHandler: emailChangeHandler,
    inputBlurHandler: emailBlurHandler,
    inputFocusHandler: emailFocusHandler,
    reset: emailRest,
    isFocus: emailIsFocus,
  } = useInput((value) => value.includes("@") && value.trim() !== "");

  const {
    value: companyNameInput,
    isValid: companyNameIsValid,
    HasError: companyNameHasError,
    inputChangeHandler: companyNameChangeHandler,
    inputBlurHandler: companyNameBlurHandler,
    inputFocusHandler: companyNameFocusHandler,
    isFocus: companyNameIsFocus,
    reset: companyRest,
  } = useInput((value) => value.trim() !== "");

  const subjectOption = [
    "Website Feedback",
    "Product or Service Inquiries",
    "Customer Support",
    "Partnership Opportunities",
    "Employment Opportunities",
    "Complaints",
    "Suggestions",
    "Other",
  ];
  const [subjectInput, setSubjectInput] = useState("");
  const subjectChangeHandler = (e) => {
    setSubjectInput(e.target.value);
    setFormWarning(""); // Clear warning on change
  };

  const {
    value: messageInput,
    isValid: messageIsValid,
    HasError: messageHasError,
    inputChangeHandler: messageChangeHandler,
    inputBlurHandler: messageBlurHandler,
    inputFocusHandler: messageFocusHandler,
    isFocus: messageIsFocus,
    reset: messageRest,
  } = useInput((value) => value.trim() !== "");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (
      !fullNameIsValid ||
      !emailIsValid ||
      !companyNameIsValid ||
      !messageIsValid
    ) {
      setFormWarning("Please fill in all the required fields.");
      return;
    }

    const formData = {
      full_name: fullNameInput,
      work_email: emailInput,
      company: companyNameInput,
      subject: subjectInput,
      message: messageInput,
    };

    try {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}contact-us-form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitSuccess(true);
        setTimeout(() => setSubmitSuccess(false), 3000); // Reset success message
        setTimeout(() => clearMessages(), 3000); // Reset success message
      } else {
        // setSubmitFailed(true);
        // setTimeout(() => setSubmitFailed(false), 3000); // Reset error message
        const errorData = await response.json();
      }
    } catch (error) {
      setSubmitFailed(true);
      setTimeout(() => setSubmitFailed(false), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const clearWarning = () => setFormWarning("");

  const clearMessages = () => {
    messageRest();
    companyRest();
    emailRest();
    fullNameRest();
  };

  return (
    <section className="scroll-section py-12 flex-1">
      <Container className="flex flex-col justify-center h-full">
        <h2 className="bebas text-5xl lg:text-6xl xl:text-7xl text-white text-csenter">
          Contact Form
        </h2>

        <form className="w-full lg:mx-auto mt-6" onSubmit={handleSubmit}>
          {formWarning && (
            <p className="text-red-500 text-center mb-4">{formWarning}</p>
          )}

          <span className="flex items-center flex-col lg:flex-row gap-4 mb-4">
            <Input
              type="text"
              label="Full Name"
              name="fullName"
              id="fullName"
              value={fullNameInput}
              onChange={(e) => {
                fullNameChangeHandler(e);
                clearWarning();
              }}
              onBlur={fullNameBlurHandler}
              onFocus={fullNameFocusHandler}
              isFocus={fullNameIsFocus}
              hasError={fullNameHasError}
              errorMessage="Full name is required"
            />
            <Input
              type="email"
              label="Work Email"
              name="email"
              id="email"
              value={emailInput}
              onChange={(e) => {
                emailChangeHandler(e);
                clearWarning();
              }}
              onBlur={emailBlurHandler}
              onFocus={emailFocusHandler}
              isFocus={emailIsFocus}
              hasError={emailHasError}
              errorMessage="Email is required"
            />
          </span>
          <span className="flex items-center flex-col lg:flex-row gap-4 mb-4">
            <Input
              type="text"
              label="Company"
              name="companyName"
              id="companyName"
              value={companyNameInput}
              onChange={(e) => {
                companyNameChangeHandler(e);
                clearWarning();
              }}
              onBlur={companyNameBlurHandler}
              onFocus={companyNameFocusHandler}
              isFocus={companyNameIsFocus}
              hasError={companyNameHasError}
              errorMessage="Company name is required"
            />
            <SelectInput
              label="Subject"
              name="subject"
              id="subject"
              options={subjectOption}
              value={subjectInput}
              onChange={subjectChangeHandler}
            />
          </span>
          <Textarea
            type="text"
            label="Your Message"
            name="yourMessage"
            id="yourMessage"
            value={messageInput}
            onChange={(e) => {
              messageChangeHandler(e);
              clearWarning();
            }}
            onBlur={messageBlurHandler}
            onFocus={messageFocusHandler}
            isFocus={messageIsFocus}
            hasError={messageHasError}
            errorMessage="Message is required"
          />

          <div className="mt-6">
            {isLoading ? (
              <div className="flex justify-center">
                <Spinner />
              </div>
            ) : (
              <button
                type="submit"
                className={`text-font  w-max msx-auto flex items-center justify-center mt-8 px-10 py-2 uppercase ${
                  submitSuccess
                    ? "bg-green-400 text-white"
                    : submitFailed
                    ? "bg-red-400 text-white"
                    : "bg-[#333]"
                }`}
              >
                {submitSuccess
                  ? "Submitted successfully"
                  : submitFailed
                  ? "Submission failed, try again"
                  : "Submit"}
              </button>
            )}
          </div>
        </form>
      </Container>
    </section>
  );
};

export default Form;
